import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, Pencil, Trash2, ChevronDown, ChevronRight, FolderOpen, Database, Layers,
  Shield, X, Settings2, Download, Upload
} from 'lucide-react';
import { toast } from 'sonner';

const OPERATORS = [
  { value: 'equals', label: '=' },
  { value: 'not_equals', label: '!=' },
  { value: 'in', label: 'IN' },
  { value: 'not_in', label: 'NOT IN' },
  { value: 'is_null', label: 'IS NULL' },
  { value: 'is_not_null', label: 'IS NOT NULL' },
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
  { value: 'between', label: 'BETWEEN' },
  { value: 'before', label: 'BEFORE (date)' },
  { value: 'after', label: 'AFTER (date)' },
];

function RuleBuilder({ rules, logicOp, onChange, availableAttrs }) {
  const addRule = () => {
    onChange({ operator: logicOp, rules: [...rules, { attribute_id: '', operator: 'equals', value: '' }] });
  };
  const removeRule = (i) => {
    onChange({ operator: logicOp, rules: rules.filter((_, idx) => idx !== i) });
  };
  const updateRule = (i, field, val) => {
    const nr = [...rules];
    nr[i] = { ...nr[i], [field]: val };
    onChange({ operator: logicOp, rules: nr });
  };
  const toggleOp = () => {
    onChange({ operator: logicOp === 'AND' ? 'OR' : 'AND', rules });
  };

  const getAttr = (id) => availableAttrs.find(a => a.attribute_id === id);

  if (availableAttrs.length === 0) {
    return <p className="text-xs text-[#9CA3AF] py-4 text-center border border-dashed border-[#E5E7EB]">No gating fields defined. Add gating fields at the group and/or schema level first.</p>;
  }

  return (
    <div className="space-y-2" data-testid="rule-builder">
      {rules.length > 1 && (
        <button onClick={toggleOp} className={`text-[10px] px-2 py-1 font-bold ${logicOp === 'AND' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`} data-testid="toggle-logic-op">
          {logicOp}
        </button>
      )}
      {rules.map((r, i) => {
        const attr = getAttr(r.attribute_id);
        const hasValueset = attr?.valueset?.codes?.length > 0;
        const noValue = ['is_null', 'is_not_null'].includes(r.operator);
        return (
          <div key={i} className="flex items-center gap-2 bg-[#F9FAFB] border border-[#E5E7EB] p-2" data-testid={`rule-${i}`}>
            {i > 0 && <span className="text-[9px] font-bold text-[#9CA3AF] w-6 shrink-0">{logicOp}</span>}
            <Select value={r.attribute_id} onValueChange={v => updateRule(i, 'attribute_id', v)}>
              <SelectTrigger className="rounded-none h-7 text-[11px] w-48 shrink-0"><SelectValue placeholder="Select gating field..." /></SelectTrigger>
              <SelectContent className="rounded-none max-h-48">
                {availableAttrs.map(a => (
                  <SelectItem key={a.attribute_id} value={a.attribute_id} className="text-[11px]">
                    <span className={`text-[9px] px-1 mr-1 ${a.source === 'group' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>{a.source === 'group' ? 'G' : 'S'}</span>
                    {a.entity_name} / {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={r.operator} onValueChange={v => updateRule(i, 'operator', v)}>
              <SelectTrigger className="rounded-none h-7 text-[11px] w-28 shrink-0"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {OPERATORS.map(o => <SelectItem key={o.value} value={o.value} className="text-[11px]">{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {!noValue && (
              hasValueset ? (
                <Select value={r.value || ''} onValueChange={v => updateRule(i, 'value', v)}>
                  <SelectTrigger className="rounded-none h-7 text-[11px] flex-1">
                    <SelectValue placeholder={`${attr.valueset.name}...`} />
                  </SelectTrigger>
                  <SelectContent className="rounded-none max-h-48">
                    {attr.valueset.codes.map(c => <SelectItem key={c.code} value={c.code} className="text-[11px]">{c.code} — {c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={r.value || ''} onChange={e => updateRule(i, 'value', e.target.value)} className="rounded-none h-7 text-[11px] flex-1" placeholder="Value..." />
              )
            )}
            <button onClick={() => removeRule(i)} className="text-[#9CA3AF] hover:text-red-500 shrink-0"><X className="w-3.5 h-3.5" /></button>
          </div>
        );
      })}
      <Button variant="outline" size="sm" onClick={addRule} className="rounded-none text-[10px] h-6" data-testid="add-rule-btn">
        <Plus className="w-3 h-3 mr-1" /> Add Rule
      </Button>
    </div>
  );
}

export default function SchemaGroupsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('edit_elements');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [expanded, setExpanded] = useState({});
  const [editGatingId, setEditGatingId] = useState(null);
  const [gatingAttrs, setGatingAttrs] = useState([]);
  const [editMember, setEditMember] = useState(null); // {group_id, schema_id, gating_logic}
  const [allSchemas, setAllSchemas] = useState([]);
  const [addMemberGroupId, setAddMemberGroupId] = useState(null);
  const [addMemberSchemaId, setAddMemberSchemaId] = useState('');
  const [editSchemaGating, setEditSchemaGating] = useState(null); // {group_id, schema_id, schema_gating_fields, schema_name}

  const fetchGroups = useCallback(async () => {
    try {
      const [gRes, sRes] = await Promise.all([api.get('/schema-groups'), api.get('/schemas')]);
      // Fetch full detail for each group
      const detailed = await Promise.all(gRes.data.map(g => api.get(`/schema-groups/${g.group_id}`).then(r => r.data)));
      setGroups(detailed);
      setAllSchemas(sRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post('/schema-groups', form);
      toast.success('Group created');
      setCreateOpen(false);
      fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (gid) => {
    if (!window.confirm('Delete this schema group?')) return;
    try { await api.delete(`/schema-groups/${gid}`); toast.success('Deleted'); fetchGroups(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddMember = async () => {
    if (!addMemberSchemaId || !addMemberGroupId) return;
    try {
      await api.post(`/schema-groups/${addMemberGroupId}/members`, { schema_id: addMemberSchemaId });
      toast.success('Schema added to group');
      setAddMemberGroupId(null);
      fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRemoveMember = async (gid, sid) => {
    try { await api.delete(`/schema-groups/${gid}/members/${sid}`); toast.success('Removed'); fetchGroups(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleExportGroup = async (gid, name) => {
    try {
      const r = await api.get(`/schema-groups/${gid}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `schema_group_${name.toLowerCase().replace(/\s+/g, '_')}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  const handleImportGroup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/schema-groups/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported "${r.data.group_name}" — created: ${JSON.stringify(r.data.created)}`);
      fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    e.target.value = '';
  };

  const openGatingEditor = async (member) => {
    try {
      const r = await api.get(`/schema-groups/${member.group_id}/available-attributes?schema_id=${member.schema_id}`);
      setGatingAttrs(r.data.attributes);
      setEditMember({ ...member, gating_logic: member.gating_logic || { operator: 'AND', rules: [] } });
    } catch { toast.error('Failed to load attributes'); }
  };

  const saveGatingLogic = async () => {
    if (!editMember) return;
    try {
      await api.put(`/schema-groups/${editMember.group_id}/members/${editMember.schema_id}`, {
        gating_logic: editMember.gating_logic,
      });
      toast.success('Gating logic saved');
      setEditMember(null);
      fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openGatingFields = async (gid) => {
    try {
      const r = await api.get(`/schema-groups/${gid}/all-attributes`);
      setGatingAttrs(r.data.attributes);
      setEditGatingId(gid);
    } catch { toast.error('Failed'); }
  };

  const openSchemaGatingFields = async (member) => {
    try {
      const r = await api.get(`/schema-groups/${member.group_id}/all-attributes`);
      setGatingAttrs(r.data.attributes);
      setEditSchemaGating({
        group_id: member.group_id, schema_id: member.schema_id,
        schema_gating_fields: member.schema_gating_fields || [],
        schema_name: member.schema_name,
      });
    } catch { toast.error('Failed'); }
  };

  const saveSchemaGatingFields = async (fields) => {
    if (!editSchemaGating) return;
    try {
      await api.put(`/schema-groups/${editSchemaGating.group_id}/members/${editSchemaGating.schema_id}`, {
        schema_gating_fields: fields,
      });
      toast.success('Schema gating fields updated');
      setEditSchemaGating(null);
      fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveGatingFields = async (gid, fields) => {
    try {
      await api.put(`/schema-groups/${gid}/gating-fields`, { gating_fields: fields });
      toast.success('Gating fields updated');
      setEditGatingId(null);
      fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) {
    return (<div className="min-h-screen bg-white"><Navbar /><div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div></div>);
  }

  return (
    <div className="min-h-screen bg-white" data-testid="schema-groups-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>Schema Groups</h1>
            <p className="text-sm text-[#4B5563] mt-1">{groups.length} groups defined</p>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 px-3 h-8 border border-[#E5E7EB] text-[#4B5563] text-xs cursor-pointer hover:bg-[#F9FAFB] transition-colors" data-testid="import-group-btn">
                <Upload className="w-3.5 h-3.5" /> Import Group
                <input type="file" accept=".zip" onChange={handleImportGroup} className="hidden" />
              </label>
              <Button onClick={() => { setForm({ name: '', description: '' }); setCreateOpen(true); }} className="rounded-none bg-[#0A0A0A] text-white hover:bg-[#171717] h-8 text-xs" data-testid="create-group-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New Group
              </Button>
            </div>
          )}
        </div>

        {groups.length === 0 && (
          <div className="border border-dashed border-[#E5E7EB] py-16 text-center">
            <FolderOpen className="w-8 h-8 text-[#9CA3AF] mx-auto mb-3" />
            <p className="text-sm text-[#4B5563]">No schema groups yet</p>
            <p className="text-xs text-[#9CA3AF] mt-1">Create groups to organize schemas (e.g., Ontada, Tempus, Tumor Registry)</p>
          </div>
        )}

        {groups.map(g => (
          <div key={g.group_id} className="border border-[#E5E7EB] mb-2" data-testid={`group-${g.group_id}`}>
            {/* Group header */}
            <div className="flex items-center gap-3 px-4 py-3 bg-[#F9FAFB]">
              <button onClick={() => setExpanded(p => ({ ...p, [g.group_id]: !p[g.group_id] }))} className="text-[#4B5563]">
                {expanded[g.group_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              <FolderOpen className="w-4 h-4 text-[#002FA7]" strokeWidth={1.5} />
              <div className="flex-1">
                <span className="text-sm font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>{g.name}</span>
                {g.description && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{g.description}</p>}
              </div>
              <span className="text-[10px] text-[#9CA3AF]">{g.member_count} schemas</span>
              {g.gating_field_count > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 font-medium"><Shield className="w-2.5 h-2.5 inline mr-0.5" />{g.gating_field_count} gating fields</span>
              )}
              {canEdit && (
                <>
                  <Button variant="ghost" size="sm" className="h-6 px-2 rounded-none text-[10px] text-blue-600" onClick={() => openGatingFields(g.group_id)} data-testid={`gating-fields-${g.group_id}`}>
                    <Shield className="w-3 h-3 mr-1" /> Gating Fields
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-2 rounded-none text-[10px] text-[#4B5563]" onClick={() => handleExportGroup(g.group_id, g.name)} data-testid={`export-group-${g.group_id}`}>
                    <Download className="w-3 h-3 mr-1" /> Export
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-none text-red-500" onClick={() => handleDelete(g.group_id)}><Trash2 className="w-3 h-3" /></Button>
                </>
              )}
            </div>

            {/* Expanded: members + gating */}
            {expanded[g.group_id] && (
              <div className="border-t border-[#E5E7EB]">
                {/* Gating fields display */}
                {(g.gating_fields || []).length > 0 && (
                  <div className="px-4 py-2 bg-blue-50/50 border-b border-[#E5E7EB]">
                    <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-[0.1em]">Group Gating Fields:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {g.gating_fields.map(gf => (
                        <span key={gf.attribute_id} className="text-[10px] px-1.5 py-0.5 bg-white border border-blue-200 text-blue-800">
                          {gf.entity_name} / {gf.attribute_name} <span className="text-blue-400">({gf.data_type})</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Members */}
                {(g.members || []).map(m => (
                  <div key={m.schema_id} className="border-b border-[#E5E7EB] last:border-b-0">
                    <div className="flex items-center gap-3 px-4 py-2.5 pl-10" data-testid={`member-${m.schema_id}`}>
                      <Database className="w-3.5 h-3.5 text-[#002FA7]" strokeWidth={1.5} />
                      <button onClick={() => navigate(`/schemas/${m.schema_id}`)} className="text-sm font-medium text-[#0A0A0A] hover:text-[#002FA7]">{m.schema_name}</button>
                      {m.version_label && <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600">{m.version_label}</span>}
                      <span className="text-[10px] text-[#9CA3AF]">{m.attribute_count} attrs</span>
                      {(m.schema_gating_fields || []).length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-700 font-medium"><Shield className="w-2.5 h-2.5 inline mr-0.5" />{m.schema_gating_fields.length} schema gating fields</span>
                      )}
                      {(m.gating_logic?.rules || []).length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700"><Settings2 className="w-2.5 h-2.5 inline mr-0.5" />{m.gating_logic.rules.length} rules</span>
                      )}
                      <div className="flex-1" />
                      {canEdit && (
                        <>
                          <Button variant="ghost" size="sm" className="h-6 px-2 rounded-none text-[10px] text-purple-600" onClick={() => openSchemaGatingFields(m)} data-testid={`schema-gating-${m.schema_id}`}>
                            <Shield className="w-3 h-3 mr-1" /> Schema Gating Fields
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 px-2 rounded-none text-[10px] text-emerald-600" onClick={() => openGatingEditor(m)} data-testid={`edit-gating-${m.schema_id}`}>
                            <Settings2 className="w-3 h-3 mr-1" /> Applicability
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-none text-red-500" onClick={() => handleRemoveMember(g.group_id, m.schema_id)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                    {/* Show schema gating fields */}
                    {(m.schema_gating_fields || []).length > 0 && (
                      <div className="px-4 py-1.5 pl-16 bg-purple-50/30 border-t border-[#E5E7EB]">
                        <div className="flex flex-wrap gap-1">
                          <span className="text-[9px] font-semibold text-purple-600 uppercase tracking-wider mr-1">Schema fields:</span>
                          {m.schema_gating_fields.map(sgf => (
                            <span key={sgf.attribute_id} className="text-[10px] px-1.5 py-0.5 bg-white border border-purple-200 text-purple-800">
                              {sgf.entity_name} / {sgf.attribute_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Add member */}
                {canEdit && (
                  <div className="px-4 py-2 pl-10 border-t border-[#E5E7EB]">
                    {addMemberGroupId === g.group_id ? (
                      <div className="flex items-center gap-2">
                        <Select value={addMemberSchemaId} onValueChange={setAddMemberSchemaId}>
                          <SelectTrigger className="rounded-none h-7 text-[11px] w-48"><SelectValue placeholder="Select schema..." /></SelectTrigger>
                          <SelectContent className="rounded-none">{allSchemas.map(s => <SelectItem key={s.schema_id} value={s.schema_id} className="text-[11px]">{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" className="h-7 rounded-none text-[10px] bg-[#002FA7]" onClick={handleAddMember}>Add</Button>
                        <Button variant="ghost" size="sm" className="h-7 rounded-none text-[10px]" onClick={() => setAddMemberGroupId(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <Button variant="ghost" size="sm" className="h-6 rounded-none text-[10px] text-[#9CA3AF]" onClick={() => { setAddMemberGroupId(g.group_id); setAddMemberSchemaId(''); }} data-testid={`add-member-${g.group_id}`}>
                        <Plus className="w-3 h-3 mr-1" /> Add Schema
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create Group Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-none max-w-sm" data-testid="create-group-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>New Schema Group</DialogTitle><DialogDescription className="sr-only">Create group</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs font-semibold text-[#4B5563]">Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="rounded-none mt-1" data-testid="group-name-input" placeholder="e.g., Ontada" /></div>
            <div><Label className="text-xs font-semibold text-[#4B5563]">Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="rounded-none mt-1" rows={2} data-testid="group-desc-input" /></div>
            <Button onClick={handleCreate} disabled={!form.name.trim()} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="save-group-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> Create Group</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gating Logic Editor Dialog */}
      <Dialog open={!!editMember} onOpenChange={() => setEditMember(null)}>
        <DialogContent className="rounded-none max-w-3xl" data-testid="gating-logic-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Gating Logic — {editMember?.schema_name}</DialogTitle><DialogDescription className="sr-only">Define applicability rules</DialogDescription></DialogHeader>
          <div className="mt-2">
            <p className="text-xs text-[#4B5563] mb-3">Define conditions under which this schema is applicable. Rules using group-level gating fields resolve at the group level. Schema-specific rules extend them.</p>
            {editMember && (
              <RuleBuilder
                rules={editMember.gating_logic?.rules || []}
                logicOp={editMember.gating_logic?.operator || 'AND'}
                onChange={gl => setEditMember(p => ({ ...p, gating_logic: gl }))}
                availableAttrs={gatingAttrs}
              />
            )}
            <Button onClick={saveGatingLogic} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585] mt-4" data-testid="save-gating-btn">Save Gating Logic</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gating Fields Editor Dialog */}
      <Dialog open={!!editGatingId} onOpenChange={() => setEditGatingId(null)}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="gating-fields-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Group Gating Fields</DialogTitle><DialogDescription className="sr-only">Select the fields needed to determine schema applicability</DialogDescription></DialogHeader>
          <div className="mt-2">
            <p className="text-xs text-[#4B5563] mb-3">Select which global attributes are needed at this group level to determine which schema applies.</p>
            <GatingFieldSelector
              availableAttrs={gatingAttrs}
              currentGroup={groups.find(g => g.group_id === editGatingId)}
              onSave={(fields) => saveGatingFields(editGatingId, fields)}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Schema Gating Fields Dialog */}
      <Dialog open={!!editSchemaGating} onOpenChange={() => setEditSchemaGating(null)}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="schema-gating-fields-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Schema Gating Fields — {editSchemaGating?.schema_name}</DialogTitle><DialogDescription className="sr-only">Additional fields for this schema</DialogDescription></DialogHeader>
          <div className="mt-2">
            <p className="text-xs text-[#4B5563] mb-3">Select additional attributes (on top of group gating fields) needed specifically for this schema's applicability logic.</p>
            {editSchemaGating && (
              <GatingFieldSelector
                availableAttrs={gatingAttrs}
                currentIds={new Set((editSchemaGating.schema_gating_fields || []).map(f => f.attribute_id))}
                onSave={(fields) => saveSchemaGatingFields(fields)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GatingFieldSelector({ availableAttrs, currentIds: initialIds, currentGroup, onSave }) {
  const computedIds = initialIds || new Set((currentGroup?.gating_fields || []).map(f => f.attribute_id));
  const [selected, setSelected] = useState(computedIds);
  const [search, setSearch] = useState('');

  const toggle = (id) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const filtered = availableAttrs.filter(a => !search || a.name.toLowerCase().includes(search.toLowerCase()) || a.entity_name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search attributes..." className="rounded-none h-8 text-sm mb-3" />
      <div className="border border-[#E5E7EB] max-h-64 overflow-auto">
        {filtered.map(a => (
          <label key={a.attribute_id} className={`flex items-center gap-2 px-3 py-1.5 border-b border-[#E5E7EB] last:border-b-0 cursor-pointer ${selected.has(a.attribute_id) ? 'bg-blue-50/50' : 'hover:bg-[#F9FAFB]'}`}>
            <input type="checkbox" checked={selected.has(a.attribute_id)} onChange={() => toggle(a.attribute_id)} className="h-3.5 w-3.5" />
            <span className="text-xs text-[#4B5563]">{a.entity_name}</span>
            <span className="text-xs font-medium text-[#0A0A0A]">{a.name}</span>
            <span className="text-[10px] text-[#9CA3AF] ml-auto">{a.data_type}</span>
          </label>
        ))}
      </div>
      <Button onClick={() => onSave([...selected].map(id => ({ attribute_id: id })))} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585] mt-3">
        Save ({selected.size} fields)
      </Button>
    </div>
  );
}
